import { createContext, useContext, useState, useMemo } from 'react';

/**
 * Context para dados normalizados da página de Places
 * Mantém cache local de professionals, services e payments
 * que são compartilhados entre todos os places
 */
const PlacesDataContext = createContext(null);

export function PlacesDataProvider({ children, data }) {
  // Cache das listas auxiliares (imutável durante a sessão da página)
  const professionals = useMemo(() => data?.professionals || [], [data?.professionals]);
  const services = useMemo(() => data?.services || [], [data?.services]);
  const payments = useMemo(() => data?.payments || [], [data?.payments]);
  const places = useMemo(() => data?.places || [], [data?.places]);

  // Criar Maps para lookup O(1) por ID
  const professionalsMap = useMemo(() => {
    const map = new Map();
    professionals.forEach(prof => map.set(prof.id, prof));
    return map;
  }, [professionals]);

  const servicesMap = useMemo(() => {
    const map = new Map();
    services.forEach(svc => map.set(svc.id, svc));
    return map;
  }, [services]);

  const paymentsMap = useMemo(() => {
    const map = new Map();
    payments.forEach(pay => map.set(pay.id, pay));
    return map;
  }, [payments]);

  // Helper functions para lookup
  const getProfessional = (id) => professionalsMap.get(id);
  const getService = (id) => servicesMap.get(id);
  const getPayment = (id) => paymentsMap.get(id);

  // Helper para enriquecer place com dados completos (para componentes legados)
  const enrichPlace = (place) => {
    if (!place) return null;

    return {
      ...place,
      // Enriquecer professionals
      professionals: place.professionals?.map(link => ({
        ...link,
        professional: getProfessional(link.professionalId),
        professionalName: getProfessional(link.professionalId)?.name,
        professionalSpecialty: getProfessional(link.professionalId)?.specialty,
      })) || [],
      
      // Enriquecer services
      services: place.services?.map(serviceLink => {
        const service = getService(serviceLink.serviceId);
        return {
          ...serviceLink,
          service,
          serviceName: service?.name,
          serviceDescription: service?.description,
          // Enriquecer payments dentro do service
          payments: serviceLink.payments?.map(paymentLink => {
            const payment = getPayment(paymentLink.paymentId);
            return {
              ...paymentLink,
              payment,
              paymentName: payment?.name,
              paymentType: payment?.type,
              paymentDisplayOrder: payment?.displayOrder,
              paymentIcon: payment?.icon,
              defaultPrice: paymentLink.price || service?.defaultPrice, // Override ou default
            };
          }) || [],
        };
      }) || [],
    };
  };

  const value = {
    // Listas originais (normalizadas)
    professionals,
    services,
    payments,
    places,
    
    // Maps para lookup rápido
    professionalsMap,
    servicesMap,
    paymentsMap,
    
    // Helper functions
    getProfessional,
    getService,
    getPayment,
    enrichPlace,
  };

  return (
    <PlacesDataContext.Provider value={value}>
      {children}
    </PlacesDataContext.Provider>
  );
}

/**
 * Hook para acessar dados normalizados de Places
 * Fornece acesso direto às listas e funções de lookup
 */
export function usePlacesData() {
  const context = useContext(PlacesDataContext);
  if (!context) {
    throw new Error('usePlacesData deve ser usado dentro de PlacesDataProvider');
  }
  return context;
}

/**
 * Hook para acessar um professional específico por ID
 */
export function useProfessional(id) {
  const { getProfessional } = usePlacesData();
  return getProfessional(id);
}

/**
 * Hook para acessar um service específico por ID
 */
export function useService(id) {
  const { getService } = usePlacesData();
  return getService(id);
}

/**
 * Hook para acessar um payment específico por ID
 */
export function usePayment(id) {
  const { getPayment } = usePlacesData();
  return getPayment(id);
}
